import React, { useState, useRef, useCallback, useEffect } from 'react';
import { View, Pressable, ScrollView, Share, Alert, Image, RefreshControl, Platform, TextInput, Keyboard } from 'react-native';
import { Text } from 'tamagui';
import HorizontalCarousel from '@components/Global/Carousel/HorizontalCarousel';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useDispatch, useSelector } from 'react-redux';
import { selectUserAvatar, selectUserId, selectUserName, selectUserPosts } from '@reduxLocal/selectors/userSelectors';
import styles from '@stylesheet';
import * as Animatable from 'react-native-animatable';
import { CountUp } from 'use-count-up';
import AntDesign from '@expo/vector-icons/AntDesign';
import { FontAwesome } from '@expo/vector-icons';
import * as Linking from 'expo-linking';
import toTitleCase from '@hooks/toTitleCase';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { LinearGradient } from 'expo-linear-gradient';
import apis from '@api/api';
import { Picker } from 'emoji-mart-native';
import handleImage from "@utils/handleImage";
import { ADD_LIKE_TO_POST, DELETE_LIKE_FROM_POST } from '@reduxLocal/actions/types';
import getDaysAgo from '@utils/getDaysAgo';
import { Avatar, AvatarGroup, Popover } from '@mui/material';
import useGlobalUser from '@hooks/getGlobalUser';
import ErrorBoundary from '@utils/ErrorBoundary';
import VideoPlayer from '@components/Global/VideoPlayer/VideoPlayer';
import { SizableText, XStack, YStack } from 'tamagui';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';
import {Card} from '@constants/Themed';
import DataCollectionRequest from './DataCollectionRequest';
import GoogleAdSenseAd from '@components/Ads/GoogleAdSenseAd';
import { extractVideoMetadata } from '@utils/videoUtils';

// Production-ready theme constants
const THEME_COLORS = {
  light: {
    primary: '#3b82f6',
    primaryLight: '#60a5fa',
    secondary: '#1e40af',
    surface: '#ffffff',
    surfaceVariant: '#f8fafc',
    onSurface: '#1e293b',
    onSurfaceVariant: '#64748b',
    border: '#e2e8f0',
    accent: '#06d6a0',
    warning: '#fbbf24',
    error: '#ef4444',
    shadow: 'rgba(0, 0, 0, 0.1)'
  },
  dark: {
    primary: '#60a5fa',
    primaryLight: '#93c5fd',
    secondary: '#3b82f6',
    surface: '#1e293b',
    surfaceVariant: '#334155',
    onSurface: '#f8fafc',
    onSurfaceVariant: '#cbd5e1',
    border: '#475569',
    accent: '#34d399',
    warning: '#fbbf24',
    error: '#f87171',
    shadow: 'rgba(0, 0, 0, 0.3)'
  }
};

const TYPOGRAPHY = {
  heading: { fontSize: 18, fontWeight: '700' as const, lineHeight: 24 },
  subheading: { fontSize: 16, fontWeight: '600' as const, lineHeight: 22 },
  body: { fontSize: 14, fontWeight: '400' as const, lineHeight: 20 },
  caption: { fontSize: 12, fontWeight: '500' as const, lineHeight: 16 },
  overline: { fontSize: 10, fontWeight: '600' as const, lineHeight: 14, letterSpacing: 0.5 }
};

const SPACING = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32
};

const SHADOWS = {
  light: {
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
  },
  medium: {
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.32,
    shadowRadius: 5.46,
  },
  featured: {
    elevation: 12,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.4,
    shadowRadius: 12,
  }
};

interface FeedComponentProps {
  item?: any;
  index?: number;
  type?: string;
  forceHorizontal?: boolean;
  refresh: () => void;
  hideAllActions?: boolean;
  hideToolbar?: boolean;
  horizontalPadding?: number;
  onDataFormClick?: (formId: string) => void;
}

const FeedComponent = ({ hideAllActions, horizontalPadding, item, index, refresh, forceHorizontal, hideToolbar, onDataFormClick }: FeedComponentProps) => {
  const post = item;
  if(!item?._id) return null;
  const _id = useSelector(selectUserId);
  const { name, surname, fullName } = useSelector(selectUserName);
  const posts = useSelector(selectUserPosts);
  const [likes, setLikes] = useState(item.likes);
  const theme = themeSelector();
  const id = item._id?.toString();
  const [fullView, setFullView] = useState(false);
  const [commentView, setCommentView] = useState(false);
  const [replyVisible, setReplyVisible] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [pendingImage, setPendingImage] = useState<{ uri?: string; base64?: string }>({});
  const likeRef = useRef<Animatable>(null);
  const responseAnimRef = useRef<Animatable>(null);
  const responseAnimRef2 = useRef<Animatable>(null);

  // Determine if this post should be featured/highlighted
  const isFeaturedPost = useCallback(() => {
    // Featured criteria:
    // 1. Sponsored posts are always featured
    if (item.type === "sponsored") return true;
    
    // 2. High engagement posts (likes + comments + shares)
    const totalEngagement = (item.likesCount || 0) + (item.comments?.length || 0) + (item.sharesCount || 0);
    if (totalEngagement >= 10) return true;
    
    // 3. Recent posts with good engagement (posted in last 24 hours with 5+ engagements)
    const postDate = new Date(item.createdAt);
    const now = new Date();
    const hoursDiff = (now.getTime() - postDate.getTime()) / (1000 * 60 * 60);
    if (hoursDiff <= 24 && totalEngagement >= 5) return true;
    
    // 4. Posts from the current user with good engagement
    if (item.postedBy?._id === _id && totalEngagement >= 3) return true;
    
    return false;
  }, [item, _id]);

  const isFeature = isFeaturedPost();
  const [topComment, setTopComment] = useState({ topComment: "", topItem: {} });
  const dispatch = useDispatch();
  const [menuPop, setMenuPop] = useState(null);
  const [showLikes, setShowLikes] = useState(null);
  const user = useGlobalUser();
  const [itemComments, setItemComments] = useState(Array.isArray(item.comments) ? item.comments : []);
  const [postLikes, setPostLikes] = useState(item.likes || []);  
  const avatar = useSelector(selectUserAvatar);

  const optionActions = [
    {
      id: 'save',
      label: 'Save',
      helpText: 'Save this post for later',
      onPress: () => { console.log('Save') },
      icon: <FontAwesome style={{ alignSelf: "center" }} name="save" size={20} color={theme ? "black" : "white"} />
    },
    {
      id: 'embed',
      label: 'Embed',
      helpText: 'Embed this post in your website',
      onPress: () => { console.log('Embed') },
      icon: <FontAwesome style={{ alignSelf: "center" }} name="code" size={20} color={theme ? "black" : "white"} />
    },
    {
      id: 'disableNotifications',
      label: 'Disable Notifications',
      helpText: 'Disable notifications for this post',
      onPress: () => { console.log('Disable Notifications') },
      icon: <FontAwesome style={{ alignSelf: "center" }} name="bell-slash" size={20} color={theme ? "black" : "white"} />
    },
    {
      id: "hide",
      label: "Hide",
      helpText: 'Hide this post from your feed',
      onPress: () => { console.log("Hide") },
      icon: <FontAwesome style={{ alignSelf: "center" }} name="eye-slash" size={20} color={theme ? "black" : "white"} />
    },
    {
      id: "report",
      label: "Report",
      helpText: 'Report this post for inappropriate content',
      onPress: () => { console.log("Report") },
      icon: <FontAwesome style={{ alignSelf: "center" }} name="exclamation-triangle" size={20} color={theme ? "black" : "white"} />
    },
    {
      id: 'block',
      label: 'Block',
      helpText: 'Block this user from your feed',
      onPress: () => { console.log('Block') },
      icon: <FontAwesome style={{ alignSelf: "center" }} name="ban" size={20} color={theme ? "black" : "white"} />
    }
  ]

  const [optionActionList, setOptionActionList] = useState(optionActions);

  useEffect(() => { findTopComment() }, [item.comments]);


  const findTopComment = () => {
    // Find comment with the most likes
    if (!item.comments || !Array.isArray(item.comments) || item.comments.length === 0) {
      setTopComment({ topComment: "", topItem: {} });
      return;
    }

    let topComment = '';
    let topItem: any;
    let topLikes = 0;
    item.comments.forEach((comment: any) => {
      if (comment?.likes > topLikes && comment?.comment) {
        topLikes = comment.likes;
        topComment = comment.comment;
        topItem = comment;
      }
    });
    if (topComment) {
      setTopComment({ topComment, topItem });
    } else if (item.comments[0]?.comment) {
      setTopComment({ topComment: item.comments[0].comment, topItem: item.comments[0] });
    } else {
      setTopComment({ topComment: "", topItem: {} });
    }
  };

  const onShare = async () => {
    const url = Linking.createURL(`/feed/${item._id}`);

    try {
      const result = await Share.share(
        {
          message: `${toTitleCase(name)} ${toTitleCase(surname)} found this awesome post! ${url}`,
          url: url,
          title: 'SmartSaaS',
        },
        {
          dialogTitle: 'Share this post',
          subject: `${toTitleCase(name)} ${toTitleCase(surname)} found this awesome post! ${url}`,
        }
      );
      if (result.action === Share.sharedAction) {
        await apis.addShareToPost(item._id);
      } else if (result.action === Share.dismissedAction) {
        // dismissed
      }
    } catch (error: any) {
      Alert.alert(error.message);
    }
  };

  const _handleLike = async () => {
    // Animation handled by Animatable.View wrapper for featured posts
    if (posts?.liked_posts?.includes(id)) {
      await apis.removeLikeFromPost(item._id).then(() => {
        setLikes(likes - 1);
        dispatch({ type: DELETE_LIKE_FROM_POST, payload: id });
        return;
      });
    } else {
      await apis.addLikeToPost(item._id).then(() => {
        setLikes(likes + 1);
        dispatch({ type: ADD_LIKE_TO_POST, payload: id });
      });
    }
    refresh?.();
  };

  const _sendReply = async (text: string) => {
    await apis.addCommentToPost(item._id, { comment: text }).then((res) => {
      const currentComments = Array.isArray(item.comments) ? item.comments : [];
      setItemComments([...currentComments, res.data]);
      setFullView(true);
      setCommentView(true);
    });
    // Animation handled by Animatable.View wrapper for featured posts
  };

  const _handleComment = useCallback(() => {
    if (!item.comments || item.comments?.length === 0) {
      return; // Animation handled by Animatable.View wrapper for featured posts
    }
    if (!fullView && !(Platform.OS === 'web')) {
      setFullView(!fullView);
      setTimeout(() => {
        setCommentView(!commentView);
      }, 300);
    }
  }, [item, commentView, fullView]);

  const buttonArray = [
    { icon: 'like', text: 'Like', onPress: () => { _handleLike(); }, count: item.likesCount || 0 },
    { icon: <FontAwesome style={{ alignSelf: "center" }} name="comments" size={20} color={theme ? "black" : "white"} />, text: 'Comment', onPress: _handleComment, count: itemComments?.length && itemComments[0]?.comment ? itemComments?.length : 0 },
    { icon: <FontAwesome style={{ alignSelf: "center" }} name="share-alt" size={20} color={theme ? "black" : "white"} />, text: 'Share', onPress: () => onShare(), count: item.sharesCount || 0 },
  ]

const _handleRemoveComment = (commentId: string) => {
  const currentComments = Array.isArray(item.comments) ? item.comments : [];
  const newComments = currentComments.filter((comment: any) => comment?._id !== commentId);
  if (newComments.length > 0 && newComments[0]?.comment) {
    setTopComment({ topComment: newComments[0].comment, topItem: newComments[0] });
  } else {
    setTopComment({ topComment: "", topItem: {} });
  }
    setItemComments(newComments);
}
 
  useEffect(() => {
    if (item.postedBy?._id === user) {
      const newActions = optionActions.filter((action) => action.id !== 'block');
      // Add delete action to top of list
      newActions.unshift({
        id: 'delete',
        label: 'Delete',
        helpText: 'Delete this post',
        onPress: async () => { await apis.deletePost(item._id).then(() => { setMenuPop(null); refresh?.(); }) },
        icon: <FontAwesome style={{ alignSelf: "center" }} name="trash" size={20} color={theme ? "black" : "white"} />
      });
      setOptionActionList(newActions);
    }
  }, []);

  // Get current theme colors
  const currentTheme = theme ? THEME_COLORS.light : THEME_COLORS.dark;

  return (
    <Animatable.View
      iterationCount={isFeature ? "infinite" : 1}
      duration={isFeature ? 3000 : 1000}
      useNativeDriver={true}
      style={{
        alignSelf: "stretch",
        flex: 1,
        // paddingHorizontal: SPACING.sm,
        paddingVertical: SPACING.xs,
        ...(isFeature && {
          shadowColor: currentTheme.primary,
          shadowOffset: { width: 0, height: 0 },
          shadowOpacity: 0.3,
          shadowRadius: 20,
          elevation: 20,
        })
      }}
    >
      <Card  
        style={{ 
          padding: 0, 
          paddingHorizontal: 0,
          marginHorizontal: -10,
          backgroundColor: currentTheme.surface, 
          borderRadius: SPACING.md, 
          ...SHADOWS.medium,
          borderWidth: 1,
          borderColor: currentTheme.border,
          overflow: 'hidden',
          ...(isFeature && {
            borderWidth: 2,
            borderColor: currentTheme.primary,
            shadowColor: currentTheme.primary,
            ...SHADOWS.featured,
            ...(Platform.OS === 'web' && {
              boxShadow: `0 0 20px ${currentTheme.primary}40, 0 0 40px ${currentTheme.primary}20, inset 0 0 20px ${currentTheme.primary}10`,
            }),
          }),
        }}
      >
        {isFeature && (
          <LinearGradient
            colors={[
              `${currentTheme.primary}15`,
              `${currentTheme.primary}08`,
              `${currentTheme.primary}15`
            ]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 0,
            }}
          />
        )}
      <ErrorBoundary>
        <YStack style={{ alignSelf: "stretch", flex: 1, minWidth: Platform.OS === 'web' ? 350 : "93%", overflow: "visible", borderRadius: SPACING.md, padding: SPACING.md, position: 'relative', zIndex: 1 }}>
          <LinearGradient  
            colors={
              theme 
                ? (item.type === "sponsored" 
                  ? [currentTheme.warning, currentTheme.primary] 
                  : [currentTheme.primary, currentTheme.primaryLight]) 
                : (item.type === "sponsored" 
                  ? [currentTheme.warning, currentTheme.primary] 
                  : [currentTheme.surfaceVariant, currentTheme.surface])
            } 
            style={{ 
              width: '100%', 
              paddingHorizontal: -35, 
              marginHorizontal: -10,
              height: item.type === "sponsored" ? 40 : 36, 
              paddingRight: 60, 
              marginTop: -SPACING.md, 
              paddingLeft: SPACING.md, 
              overflow: "visible", 
              borderTopLeftRadius: SPACING.md, 
              borderTopRightRadius: SPACING.md,
              flexDirection: "row", 
              alignItems: 'center',
              paddingVertical: SPACING.sm,
            }}
          >
            <XStack style={{ flex: 1, alignItems: 'center', justifyContent: 'space-between' }}>
              <View style={{ flex: 1 }}>
                {item.type === "sponsored" ? (
                  <Text style={{
                    ...TYPOGRAPHY.subheading,
                    color: 'white',
                    textShadowRadius: 10,
                    textShadowColor: 'rgba(0,0,0,0.3)',
                    textShadowOffset: { width: 1, height: 1 }
                  }}>
                    {toTitleCase(item.postedBy?.profile_information?.name)} {toTitleCase(item.postedBy?.profile_information?.surname) || item.company?.company_name || ""}
                  </Text>
                ) : (
                  <Text numberOfLines={1} style={{
                    ...TYPOGRAPHY.caption,
                    color: 'white',
                    fontWeight: '600'
                  }}>
                    {toTitleCase(item.postedBy?.profile_information?.name)} {toTitleCase(item.postedBy?.profile_information?.surname)}
                  </Text>
                )}
              </View>
              {isFeature && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.xs }}>
                  <Text style={{
                    ...TYPOGRAPHY.overline,
                    color: '#FFD700',
                    fontWeight: '700',
                    textShadowRadius: 8,
                    textShadowColor: '#FFA500',
                    textShadowOffset: { width: 1, height: 1 }
                  }}>
                    ✨ FEATURED
                  </Text>
                </View>
              )}
            </XStack>
          </LinearGradient>
          
          {/* Avatar and Menu Section */}
          <Pressable 
            onPress={(ev) => { setMenuPop(ev.currentTarget) }} 
            style={{ 
              position: 'absolute', 
              right: SPACING.sm, 
              top: SPACING.xs, 
              flexDirection: "row",
              alignItems: 'center',
              gap: SPACING.xs,
              zIndex: 2,
              paddingLeft: SPACING.sm,
              borderRadius: SPACING.md,
              backgroundColor: 'rgba(255,255,255,0.1)',
              ...(Platform.OS === 'web' && { backdropFilter: 'blur(10px)' } as any),
            }}
          >
            <Text style={{ 
              ...TYPOGRAPHY.overline, 
              color: "white",
              textShadowRadius: 4,
              textShadowColor: 'rgba(0,0,0,0.5)'
            }}>
              {getDaysAgo(item.createdAt)}
            </Text>
            <AvatarHandler 
              avatar={item.postedBy?.profile_information?.avatar.img || "https://storage.googleapis.com/smartsaas-avatar-store/Asset%202.png"} 
              avatarImageStyle={{ 
                width: item.type === "sponsored" ? 50 : 44, 
                height: item.type === "sponsored" ? 50 : 44, 
                borderRadius: item.type === "sponsored" ? 25 : 22,
                borderWidth: 3, 
                borderColor: 'white',
                ...SHADOWS.medium
              }} 
            />
          </Pressable>

          {/* Popover Menu for Web */}
          {Platform.OS === 'web' && (
            <Popover
              open={!!menuPop}
              onClose={() => { setMenuPop(null) }}
              anchorEl={menuPop}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                style: { 
                  borderRadius: SPACING.md,
                  ...SHADOWS.medium,
                  backgroundColor: currentTheme.surface,
                  border: `1px solid ${currentTheme.border}`
                }
              }}
            >
              {optionActionList.map((action, index) => {
                const [hovered, setHovered] = useState(false);
                return (
                  <Pressable 
                    key={index} 
                    onPress={action.onPress} 
                    onHoverIn={() => setHovered(true)} 
                    onHoverOut={() => setHovered(false)} 
                    style={{
                      backgroundColor: hovered ? currentTheme.surfaceVariant : 'transparent',
                      flexDirection: 'row',
                      padding: SPACING.md,
                      alignItems: 'center',
                      gap: SPACING.md,
                      minWidth: 200
                    }}
                  >
                    <View style={{ width: 24, height: 24, alignItems: 'center', justifyContent: 'center' }}>
                      {action.icon}
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={{ 
                        ...TYPOGRAPHY.body, 
                        color: currentTheme.onSurface,
                        fontWeight: '600'
                      }}>
                        {action.label}
                      </Text>
                      <Text style={{ 
                        ...TYPOGRAPHY.overline, 
                        color: currentTheme.onSurfaceVariant 
                      }}>
                        {action.helpText}
                      </Text>
                    </View>
                  </Pressable>
                );
              })}
            </Popover>
          )}

          {/* Content Section */}
          <View style={{ 
            paddingHorizontal: SPACING.md, 
            paddingTop: SPACING.lg, 
            paddingBottom: SPACING.md,
            gap: SPACING.md 
          }}>
              {/* Google AdSense Ad - Render if this is a Google AdSense ad */}
              {item.isExternalAd && item.adNetwork === 'google_adsense' && Platform.OS === 'web' ? (
                <View style={{
                  width: '100%',
                  minHeight: 250,
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginVertical: SPACING.md
                }}>
                  <GoogleAdSenseAd
                    adSlot={item.adSlot || undefined}
                    adFormat="auto"
                    fullWidthResponsive={true}
                    adStyle={{
                      display: 'block',
                      minHeight: 250,
                      width: '100%'
                    }}
                  />
                </View>
              ) : (
                  <SizableText style={{
                    ...TYPOGRAPHY.body,
                    color: currentTheme.onSurface,
                    lineHeight: 22,
                    minHeight: 20
                  }}>
                    {item.content}
                  </SizableText>
              )}
            
              {/* URL Link - exclude video URLs */}
              {item.url && !extractVideoMetadata(item.url) && (
              <Pressable 
                onPress={() => {
                  if (Platform.OS === 'web') {
                    window.open(item.url, '_blank')
                  } else { 
                    Linking.openURL(item.url) 
                  }
                }} 
                style={{ 
                  flexDirection: 'row', 
                  alignItems: 'center',
                  gap: SPACING.xs,
                  padding: SPACING.sm,
                  backgroundColor: currentTheme.surfaceVariant,
                  borderRadius: SPACING.sm,
                  borderWidth: 1,
                  borderColor: currentTheme.border
                }}
              >
                <FontAwesome 
                  name="external-link" 
                  size={16} 
                  color={currentTheme.primary} 
                />
                <Text 
                  numberOfLines={1} 
                  style={{ 
                    ...TYPOGRAPHY.caption,
                    color: currentTheme.primary,
                    textDecorationLine: 'underline',
                    flex: 1
                  }}
                >
                  {item.url}
                </Text>
              </Pressable>
            )}

              {/* Data Form Link */}
              {item.data_link && item.data_link._id && (
                <Pressable
                  onPress={() => {
                    if (onDataFormClick) {
                      onDataFormClick(item.data_link._id);
                    }
                  }}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: SPACING.md,
                    padding: SPACING.md,
                    backgroundColor: theme ? 'rgba(90,192,234,0.08)' : 'rgba(90,192,234,0.15)',
                    borderRadius: SPACING.md,
                    borderWidth: 2,
                    borderColor: currentTheme.primary,
                    marginTop: SPACING.sm,
                    ...SHADOWS.light
                  }}
                >
                  <View style={{
                    width: 48,
                    height: 48,
                    borderRadius: 24,
                    backgroundColor: currentTheme.primary,
                    alignItems: 'center',
                    justifyContent: 'center',
                    ...SHADOWS.light
                  }}>
                    <FontAwesome
                      name="wpforms"
                      size={24}
                      color="white"
                    />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text
                      style={{
                        ...TYPOGRAPHY.subheading,
                        fontWeight: "700",
                        color: currentTheme.primary,
                        marginBottom: 4
                      }}
                    >
                      📋 Fill out this form
                    </Text>
                    <Text
                      numberOfLines={1}
                      style={{
                        ...TYPOGRAPHY.caption,
                        color: currentTheme.onSurfaceVariant
                      }}
                    >
                      {item.data_link.dataTitle || 'Untitled Form'}
                    </Text>
                  </View>
                  <FontAwesome
                    name="chevron-right"
                    size={20}
                    color={currentTheme.primary}
                  />
                </Pressable>
              )}

              {/* Data Collection Request */}
              {item.data_link && (item.data_link._id || typeof item.data_link === 'string') && (
                <DataCollectionRequest
                  dataLink={typeof item.data_link === 'string'
                    ? { _id: item.data_link, dataTitle: 'Dataset' }
                    : item.data_link}
                  theme={theme}
                  currentTheme={currentTheme}
                  responsive={{
                    spacing: SPACING,
                    typography: {
                      xs: 12,
                      sm: 14,
                      base: 16,
                      lg: 18,
                    },
                    components: {
                      iconSize: 20,
                    },
                  }}
                  TYPOGRAPHY={TYPOGRAPHY}
                  SHADOWS={SHADOWS}
                />
              )}

            {/* Media Content */}
            {post.images?.length > 0 ? (
              <View style={{ 
                marginHorizontal: -SPACING.md, 
                borderRadius: SPACING.sm,
                overflow: 'hidden',
                ...SHADOWS.light
              }}>
                <HorizontalCarousel
                  images={item.images || null}
                  height={300}
                />
              </View>
            ) : (
              <>
                    {/* External Video Support (YouTube, TikTok, Vimeo) */}
                    {(item.type === 'external_video' || extractVideoMetadata(item.url || '')) && (
                  <View style={{ 
                        marginHorizontal: -SPACING.md,
                    borderRadius: SPACING.sm,
                    overflow: 'hidden',
                        ...SHADOWS.light,
                  }}>
                        <VideoPlayer
                          url={item.external_video?.url || item.url}
                          thumbnail={item.external_video?.thumbnail}
                          title={item.external_video?.title}
                          provider={item.external_video?.provider}
                          videoId={item.external_video?.videoId}
                          height={300}
                          autoPlay={false}
                        />
                  </View>
                )}
              </>
            )}
          </View>
          {/* <View style={{ flex: 4, borderRadius: 5 }}>
            {
              commentView &&
              <View style={{ flex: 4, marginTop: -30 }}>
                <View style={[styles.centerEverything, { alignSelf: "stretch", flexDirection: 'row', backgroundColor: 'grey', padding: 10, maxHeight: 40, }]}>
                    {
                      aiResultText ?
                        <View style={{ flex: 10 }}>
                          {
                            aiReponseLoading ?
                              <RefreshControl style={{ flex: 10, alignContent: 'flex-end', alignItems: 'flex-end', alignSelf: 'flex-end' }} tintColor={'white'} title='Refreshing Feed' titleColor={'white'} refreshing={aiReponseLoading} colors={['rgb(90,192,234)']} />
                              :
                              <View style={{ flex: 5 }}>
                                <AnimatedTextFunction key={aiResultText} style={{ flex: 10, color: 'white' }} fontColor='white' value={aiResultText} fontSize={12} />
                              </View>
                          }
                        </View>
                        :
                        <View style={{ flex: 3 }} />
                    }
                    <Pressable style={{ flex: 1 }} onPress={async () => { setAIResponseLoading(true); await apis.getAICommentAnalysis(item._id).then((text) => { setAIResponseLoading(false); setAIResultText(text.toString()) }) }}>
                      <AnimatedLottieView source={require('@assets/animations/automation_robot.json')} autoPlay style={{ minHeight: 30, width: 30 }} />
                    </Pressable>
                  </View>
                  <ScrollView contentContainerStyle={{ left: -25 }} style={{ maxHeight: 400, paddingVertical: 10, backgroundColor: theme ? "white" : "grey", paddingBottom: 135 }}>
                    <CommentsFlatList removeCallback={(commentId) => { _handleRemoveComment(commentId) }} feedId={item._id} comments={itemComments} />
                  </ScrollView>
                </View>
            }
          </View> */}
          {/* Action Buttons Section */}
          {Platform.OS === 'web' && !hideAllActions && (
            <View style={{ 
              marginHorizontal: -SPACING.md,
              marginBottom: item.type === 'sponsored' ? -SPACING.md : 0,
              borderTopWidth: 1,
              borderTopColor: currentTheme.border,
              backgroundColor: currentTheme.surfaceVariant
            }}>
              <View style={{
                flexDirection: "row",
                justifyContent: "space-around",
                paddingVertical: SPACING.md,
                paddingHorizontal: SPACING.sm,
                borderBottomLeftRadius: item.type === 'sponsored' ? SPACING.md : 0,
                borderBottomRightRadius: item.type === 'sponsored' ? SPACING.md : 0,
              }}>
                {buttonArray.map((buttonItem, index) => {
                  if (post.type === 'sponsored' && buttonItem.text === 'Comment') return null;
                  
                  return (
                    <View 
                      key={index} 
                      style={{ 
                        flexDirection: "row", 
                        flex: 1, 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        gap: SPACING.xs
                      }} 
                    >
                      {/* Likes Avatar Group */}
                      {post.likes?.length > 0 && buttonItem.icon === 'like' && (
                        <Pressable 
                          style={{ marginRight: SPACING.xs }} 
                          onPress={(ev) => { setShowLikes(ev.currentTarget) }}
                        >
                          <AvatarGroup 
                            max={4} 
                            spacing="small"
                            sx={{ 
                              '& .MuiAvatar-root': { 
                                width: 28, 
                                height: 28, 
                                border: `2px solid ${currentTheme.surface}`,
                                ...SHADOWS.light
                              } 
                            }}
                          >
                            {postLikes?.slice(0, 4).map((like, likeIndex) => (
                              like?.profile_information?.avatar?.img && (
                                <Avatar 
                                  key={`like-${post._id}-${like._id}-${likeIndex}`}
                                  src={like.profile_information.avatar.img} 
                                  sx={{ width: 28, height: 28 }}
                                />
                              )
                            ))}
                          </AvatarGroup>
                        </Pressable>
                      )}

                      {/* Likes Popover */}
                      <Popover
                        open={!!showLikes}
                        onClose={() => { setShowLikes(null) }}
                        anchorEl={showLikes}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        PaperProps={{
                          style: { 
                            borderRadius: SPACING.md,
                            ...SHADOWS.medium,
                            backgroundColor: currentTheme.surface,
                            border: `1px solid ${currentTheme.border}`,
                            maxWidth: 320,
                            maxHeight: 400
                          }
                        }}
                      >
                        <ScrollView style={{ maxHeight: 350, padding: SPACING.sm }}>
                          <Text style={{
                            ...TYPOGRAPHY.subheading,
                            color: currentTheme.onSurface,
                            textAlign: 'center',
                            paddingBottom: SPACING.md,
                            borderBottomWidth: 1,
                            borderBottomColor: currentTheme.border,
                            marginBottom: SPACING.md
                          }}>
                            Liked by {postLikes?.length} {postLikes?.length === 1 ? 'person' : 'people'}
                          </Text>
                          {postLikes?.map((like, likeIndex) => (
                            <View 
                              key={`like-pop-${like._id}-${likeIndex}`} 
                              style={{ 
                                flexDirection: "row", 
                                alignItems: 'center',
                                padding: SPACING.sm,
                                gap: SPACING.md,
                                borderRadius: SPACING.sm,
                                marginBottom: SPACING.xs
                              }}
                            >
                              <Avatar 
                                src={like?.profile_information?.avatar?.img} 
                                sx={{ width: 36, height: 36 }}
                              />
                              <Text style={{ 
                                ...TYPOGRAPHY.body,
                                color: currentTheme.onSurface,
                                flex: 1
                              }}>
                                {toTitleCase(like?.profile_information?.name)} {toTitleCase(like?.profile_information?.surname)}
                              </Text>
                            </View>
                          ))}
                        </ScrollView>
                      </Popover>

                      {/* Action Button */}
                      <Pressable 
                        onPress={buttonItem.onPress} 
                        disabled={buttonItem.icon === "comments" && Platform.OS === 'web'}
                        style={({ pressed }) => ({
                          flexDirection: "row", 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          gap: SPACING.xs,
                          paddingVertical: SPACING.sm,
                          paddingHorizontal: SPACING.md,
                          borderRadius: SPACING.lg,
                          backgroundColor: pressed ? currentTheme.border : 'transparent',
                          opacity: (buttonItem.icon === "comments" && Platform.OS === 'web') ? 0.5 : 1
                        })}
                      >
                        {typeof buttonItem.icon === 'string' ? (
                          <Animatable.View 
                            animation={posts?.liked_posts?.includes(id) && buttonItem.icon === 'like' ? "bounceIn" : undefined}
                            duration={600}
                          >
                            <AntDesign 
                              name={buttonItem.icon as any} 
                              size={20} 
                              color={
                                posts?.liked_posts?.includes(id) && buttonItem.icon === 'like' 
                                  ? currentTheme.accent 
                                  : currentTheme.onSurfaceVariant
                              } 
                            />
                          </Animatable.View>
                        ) : (
                          <Animatable.View>
                            {buttonItem.icon}
                          </Animatable.View>
                        )}
                        
                        {buttonItem.count !== undefined ? (
                          <Text style={{ 
                            ...TYPOGRAPHY.caption,
                            color: currentTheme.onSurfaceVariant,
                            fontWeight: '600'
                          }}>
                            <CountUp isCounting start={0} end={buttonItem.count} />
                          </Text>
                        ) : (
                          <Text style={{ 
                            ...TYPOGRAPHY.caption,
                            color: currentTheme.onSurfaceVariant,
                            fontWeight: '500'
                          }}>
                            {buttonItem.text}
                          </Text>
                        )}
                      </Pressable>
                    </View>
                  );
                })}
              </View>
              
              {/* Comment Input Section */}
              {item.type !== "sponsored" && !hideToolbar && (
                <View style={{ 
                  borderTopWidth: 1,
                  borderTopColor: currentTheme.border,
                  backgroundColor: currentTheme.surface,
                  borderBottomLeftRadius: SPACING.md,
                  borderBottomRightRadius: SPACING.md,
                    overflow: 'hidden',
                }}>
                    {/* Inline Emoji Picker */}
                    {showEmojiPicker && (
                      <View style={{
                        padding: SPACING.md,
                        backgroundColor: currentTheme.surfaceVariant,
                        borderBottomWidth: 1,
                        borderBottomColor: currentTheme.border,
                      }}>
                        <Picker
                          onSelect={(emoji) => {
                            setReplyText(prev => prev + emoji.native);
                            setShowEmojiPicker(false);
                          }}
                          theme={theme ? 'light' : 'dark'}
                        />
                      </View>
                    )}

                    {/* Inline Input with Tools */}
                    <View style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      paddingHorizontal: SPACING.md,
                      paddingVertical: SPACING.sm,
                      backgroundColor: currentTheme.surfaceVariant,
                      gap: SPACING.sm,
                    }}>
                      {/* Avatar */}
                      {index === 0 && (
                        <AvatarHandler
                          avatar={avatar?.img || "https://storage.googleapis.com/smartsaas-avatar-store/Asset%202.png"}
                          avatarImageStyle={{
                            width: 36,
                            height: 36,
                            borderRadius: 18,
                          }}
                        />
                      )}

                      {/* Text Input */}
                      <View style={{
                        flex: 1,
                        flexDirection: 'row',
                        alignItems: 'center',
                        backgroundColor: currentTheme.surface,
                        borderRadius: SPACING.lg,
                        paddingHorizontal: SPACING.md,
                        paddingVertical: SPACING.xs,
                        borderWidth: 1,
                        borderColor: currentTheme.border,
                        minHeight: 40,
                      }}>
                        <TextInput
                          style={{
                            flex: 1,
                            color: currentTheme.onSurface,
                            fontSize: 14,
                            paddingVertical: SPACING.xs,
                          }}
                          placeholder={item.comments?.length > 0
                            ? "💬 Leave a comment" 
                            : "💬 Be the first to leave a reply"}
                          placeholderTextColor={currentTheme.onSurfaceVariant}
                          value={replyText}
                          onChangeText={setReplyText}
                          multiline
                          maxLength={500}
                        />

                        {/* Inline Tools - Emoji Picker */}
                        <Pressable
                          onPress={() => setShowEmojiPicker(!showEmojiPicker)}
                          style={{
                            padding: SPACING.xs,
                            marginLeft: SPACING.xs,
                          }}
                        >
                          <FontAwesome
                            name="smile-o"
                            size={20}
                            color={currentTheme.onSurfaceVariant}
                          />
                        </Pressable>

                        {/* Inline Tools - Image Attachment */}
                        <Pressable
                          onPress={async () => {
                            await handleImage(true).then(res => {
                              if (typeof res === 'string') {
                                setPendingImage({ uri: res });
                              } else if (res && typeof res === 'object') {
                                setPendingImage({ uri: res.uri, base64: res.base64 });
                              }
                            });
                          }}
                          style={{
                            padding: SPACING.xs,
                            marginLeft: SPACING.xs,
                          }}
                        >
                          <FontAwesome
                            name="image"
                            size={20}
                            color={currentTheme.onSurfaceVariant}
                          />
                        </Pressable>
                      </View>

                      {/* Send Button */}
                      {(replyText.trim().length > 0 || pendingImage?.base64) && (
                        <Pressable
                          onPress={() => {
                            if (replyText.trim().length > 0 || pendingImage?.base64) {
                              _sendReply(replyText);
                              setReplyText('');
                              setPendingImage({});
                              Keyboard.dismiss();
                            }
                          }}
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 18,
                            backgroundColor: currentTheme.primary,
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginLeft: SPACING.xs,
                          }}
                        >
                          <AntDesign
                            name="arrow-up"
                            size={18}
                            color="white"
                          />
                        </Pressable>
                      )}
                    </View>

                    {/* Pending Image Preview */}
                    {pendingImage?.uri && (
                      <View style={{
                        padding: SPACING.md,
                        backgroundColor: currentTheme.surfaceVariant,
                        borderTopWidth: 1,
                        borderTopColor: currentTheme.border,
                      }}>
                        <View style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: SPACING.sm,
                        }}>
                          <Image
                            source={{ uri: pendingImage.uri }}
                            style={{
                              width: 50,
                              height: 50,
                              borderRadius: SPACING.sm,
                            }}
                          />
                          <View style={{ flex: 1 }}>
                            <Text style={{
                              ...TYPOGRAPHY.caption,
                              color: currentTheme.onSurfaceVariant,
                            }}>
                              Image attached
                            </Text>
                          </View>
                          <Pressable
                            onPress={() => setPendingImage({})}
                            style={{
                              padding: SPACING.xs,
                            }}
                          >
                            <AntDesign
                              name="close"
                              size={18}
                              color={currentTheme.error}
                            />
                          </Pressable>
                        </View>
                      </View>
                    )}
                </View>
              )}
            </View>
          )}
        </YStack>
    </ErrorBoundary>
    </Card>
    </Animatable.View>
  )
}

export default FeedComponent;